import React from 'react';
import { useGlobalization } from '@Translations/useGlobalization';
import { StyledGrid } from '@Utils/styles';
import { DropDownButton, ZIndex, Text } from '@innovyze/stylovyze';
import { Button, Grid, Stack } from '@mui/material';

export const ClassesButton = (
	isLoading: boolean,
	onSetRangeClasses: (classes: number) => void,
	rangeClasses: number,
) => {
	const { t } = useGlobalization();

	const classOptions = () => {
		const options = [];
		for (let i = 1; i <= 10; ++i) {
			options.push({
				onClick: () => {
					onSetRangeClasses(i);
				},
				name: t('{{classes}} Classes', { classes: i + 2 }),
			});
		}
		return options;
	};

	return (
		<DropDownButton
			key={Date.now()}
			options={classOptions()}
			initialSelectedIndex={rangeClasses - 1}
			color="primary"
			variant="outlined"
			fitMenuToContent
			fitButtonToContent
			disabled={isLoading}
			popupZIndex={ZIndex.DropDownButtonMenu}
		/>
	);
};

interface TableToolbarProps {
	isLoading: boolean;
	isUnique: boolean;
	hideCounts: boolean;
	onSetRangeClasses: (classes: number) => void;
	onRecalculateCount: () => void;
	rangeClasses: number;
	recalculateOnly: boolean;
	forComponent: boolean;
}

export const TableToolbar = ({
	isLoading,
	isUnique,
	hideCounts,
	onSetRangeClasses,
	onRecalculateCount,
	rangeClasses,
	recalculateOnly,
	forComponent,
}: TableToolbarProps) => {
	const { t } = useGlobalization();

	let heading = '';
	let subHeading = '';

	if (forComponent) {
		heading = t('Score Setup');
		subHeading = t(
			'Set your scores below to complete the component setup.',
		);
	} else {
		heading = t('Parameter Values');
		if (isUnique) {
			subHeading = t(
				'These are the unique values that will be used in the score setup.',
			);
		} else {
			subHeading = t(
				'These are the ranges that will be used in the score setup. You can edit the number of classes shown and the values in the table.',
			);
		}
	}

	return (
		<StyledGrid
			container
			style={{
				width: '100%',
				marginTop: '1rem',
				marginBottom: '1rem',
			}}>
			<Grid item xs={6}>
				<Text variant="heading-large-strong">{heading}</Text>
				<Text variant="caption-medium">{subHeading}</Text>
			</Grid>
			<Grid item xs={6} display="flex" justifyContent="flex-end">
				<Stack direction={'row'} spacing={'20px'}>
					{hideCounts ? (
						[]
					) : (
						<div>
							<Button
								disabled={isLoading}
								variant="outlined"
								onClick={() => {
									onRecalculateCount();
								}}>
								{t('Recalculate Asset Count')}
							</Button>
						</div>
					)}
					{!recalculateOnly &&
						!isUnique &&
						ClassesButton(
							isLoading,
							onSetRangeClasses,
							rangeClasses,
						)}
				</Stack>
			</Grid>
		</StyledGrid>
	);
};

export const TableToolbarUnique = ({ forComponent }: TableToolbarProps) => {
	const { t } = useGlobalization();

	let heading = '';
	let subHeading = '';

	if (forComponent) {
		heading = t('Score Setup');
		subHeading = t(
			'Set your scores below to complete the component setup.',
		);
	} else {
		heading = t('Parameter Values');
		subHeading = t(
			'These are the unique values that will be used in the score setup.',
		);
	}

	return (
		<StyledGrid
			container
			style={{
				width: '100%',
				marginTop: '1rem',
				marginBottom: '1rem',
			}}>
			<Grid item xs={8}>
				<Text variant="heading-large-strong">{heading}</Text>
				<Text variant="caption-medium">{subHeading}</Text>
			</Grid>
			<Grid item xs={4} display="flex" justifyContent="flex-end"></Grid>
		</StyledGrid>
	);
};
